<template>
  <div>
    <h1>Template Selector</h1>

    <div class="p-base bg-signableBrandColours-white mb-xl">
      <h2 class="text-lg font-bold">No templates</h2>

      <TemplateSelectorScreen
        :templates="[]"
        :totalTemplates="0"
        :pendingTemplates="false"
        :fetchingTemplates="false"
        :fetchingNextTemplates="false"
        :placeholderTemplates="false"
        :hasMore="false"
        :selectedTemplates="[]"
        class="max-w-[44rem] mx-auto mt-base"
        listClass="max-h-[50vh] overflow-y-auto"
      />
    </div>

    <div class="p-base bg-signableBrandColours-white mb-xl">
      <h2 class="text-lg font-bold">No results</h2>

      <TemplateSelectorScreen
        ref="noResults"
        :templates="[]"
        :totalTemplates="0"
        :pendingTemplates="false"
        :fetchingTemplates="false"
        :fetchingNextTemplates="false"
        :placeholderTemplates="false"
        :hasMore="false"
        :selectedTemplates="[]"
        class="max-w-[44rem] mx-auto mt-base"
        listClass="max-h-[50vh] overflow-y-auto"
      />
    </div>

    <div class="p-base bg-signableBrandColours-white mb-xl">
      <h2 class="text-lg font-bold">Load more button</h2>
      <p>The button is a fallback in case the auto loading doesn't trigger, not normally visible to users.</p>

      <TemplateSelectorScreen
        :templates="twelveTemplates"
        :totalTemplates="18"
        :pendingTemplates="false"
        :fetchingTemplates="false"
        :fetchingNextTemplates="false"
        :placeholderTemplates="false"
        :hasMore="true"
        :selectedTemplates="[]"
        class="max-w-[44rem] mx-auto mt-base"
        listClass="max-h-[50vh] overflow-y-auto"
      />
    </div>

    <div class="p-base bg-signableBrandColours-white mb-xl">
      <h2 class="text-lg font-bold">Loading next results</h2>

      <TemplateSelectorScreen
        :templates="twelveTemplates"
        :totalTemplates="18"
        :pendingTemplates="false"
        :fetchingTemplates="false"
        :fetchingNextTemplates="true"
        :placeholderTemplates="false"
        :hasMore="true"
        :selectedTemplates="[]"
        class="max-w-[44rem] mx-auto mt-base"
        listClass="max-h-[50vh] overflow-y-auto"
      />
    </div>

    <div class="p-base bg-signableBrandColours-white mb-xl">
      <h2 class="text-lg font-bold">Search templates</h2>
      <p><strong>The search results are faked!!</strong></p>
      <p>The longer the search text the more pages of results you will get</p>

      <TemplateSelectorScreen
        :templates="infiniteTemplates"
        :totalTemplates="totalTemplates"
        :pendingTemplates="isPendingTemplates"
        :fetchingTemplates="isFetchingTemplates"
        :fetchingNextTemplates="isFetchingNextTemplates"
        :placeholderTemplates="isPlaceholderTemplates"
        :hasMore="hasNextTemplates"
        @search="searchTemplates"
        @next="fetchNextTemplates"
        :selectedTemplates="selectedTemplates"
        @add="addTemplate"
        @remove="removeTemplate"
        class="max-w-[44rem] mx-auto mt-base"
        listClass="max-h-[50vh] overflow-y-auto"
      />

      <p
        v-if="selectedTemplates.length"
        class="my-base"
      >
        <b>Selected templates:</b>
        {{ selectedTemplates.map((template) => template.title).join(', ') }}
      </p>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { computed, onMounted, ref } from 'vue';
import { useReactiveMap } from 'vue-reactive-collection';

import simpleHash from '@/utils/simpleHash';
import { useInfiniteTemplates } from '@/composables/templates';
import { createListTemplates } from '@/data/factories/templates';

import TemplateSelectorScreen from '@/components/screens/envelopeCreate/TemplateSelectorScreen.vue';

/** @typedef {import('@/store/modules/bulkSend/models/Templates').TListTemplate} TListTemplate */
/** @typedef {import('@/queries/templates/queryKeys').TAllTemplatesKey} TAllTemplatesKey */
/** @typedef {import('@/queries/templates/queryKeys').TSearchTemplatesKey} TSearchTemplatesKey */

const query = ref();

const howMany = computed(() => (query.value?.length || 10) * 6);

/**
 * Queries the templates based on the provided parameters.
 *
 * @param {Object} options - The options for the query.
 * @param {TAllTemplatesKey|TSearchTemplatesKey} options.queryKey - The query key.
 * @param {number} options.pageParam - The page parameter.
 */
async function listTemplates({ queryKey, pageParam }) {
  const [, search] = queryKey;

  // Simulate a network request.
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, Math.random() * 500);
  });

  return {
    templates: createListTemplates(24, {
      seed: parseInt(`${simpleHash(search || '') || 0}${pageParam}`, 10),
    }).map(template => ({
      ...template,
      title: `${template.title} ${query.value || ''}`.trim(),
    })),
    meta: {
      total: howMany.value,
      current_page: pageParam,
      last_page: Math.ceil(howMany.value / 24),
      path: '',
      to: 0,
      from: 0,
      links: [],
      per_page: 24,
    },
  };
}

const {
  templates: infiniteTemplates,
  totalTemplates,
  fetchNextTemplates,
  hasNextTemplates,
  isPendingTemplates,
  isFetchingTemplates,
  isFetchingNextTemplates,
  isPlaceholderTemplates,
} = useInfiniteTemplates(query, true, listTemplates);

function searchTemplates(search) {
  query.value = search;
}

/** @type {import('vue').Ref<Map<string, string>>} */
const templatesMap = useReactiveMap();
function addTemplate(fingerprint, title) {
  templatesMap.value.set(fingerprint, title);
}
function removeTemplate(fingerprint) {
  templatesMap.value.delete(fingerprint);
}
/** @type {import('vue').ComputedRef<{fingerprint: string, title: string}[]>} */
const selectedTemplates = computed(() =>
  [...templatesMap.value.entries()].map(([fingerprint, title]) => ({
    fingerprint,
    title,
  })));

const twelveTemplates = createListTemplates(12);

// Hacky way to set up the state for the no results screen.
const noResults = ref();
onMounted(() => {
  if (!noResults.value) {
    return;
  }
  noResults.value.$children[0].search = 'not a real search';
  noResults.value.$children[0].onSearch();
});
</script>
